import { Typography, Box } from '@mui/material'
import { PaymentElement, useStripe, useElements, AddressElement } from '@stripe/react-stripe-js'
import { Stripe, StripeElements, StripePaymentElementOptions } from '@stripe/stripe-js'
import React, { useState, useEffect, Dispatch, SetStateAction } from 'react'

import TaxForm from './TaxForm'
import { ITaxDetailsPayload, useTierSelectionContext } from '../../hooks/useTierSelection'

export function CheckoutForm({
  clientSecret,
  showTaxForm,
  onStripeLoaded,
  onPaymentSuccess,
  showAddressForm,
  setIsCardComplete,
  setIsAddressComplete,
  taxDetails,
  handleTaxDetailsChange,
  setInvalidTaxDetails,
}: {
  clientSecret: string
  showTaxForm?: boolean
  showAddressForm?: boolean
  onStripeLoaded: (stripeObject: { elements: StripeElements; stripe: Stripe }) => void
  onPaymentSuccess: () => void
  setIsCardComplete?: Dispatch<SetStateAction<boolean>>
  setIsAddressComplete?: Dispatch<SetStateAction<boolean>>
  taxDetails?: ITaxDetailsPayload
  handleTaxDetailsChange?: (type: string, value: string) => void
  setInvalidTaxDetails?: Dispatch<SetStateAction<boolean>>
}) {
  const stripe = useStripe()
  const elements = useElements()
  const { customerProfile } = useTierSelectionContext()

  const [message, setMessage] = useState<string | null>(null)
  const [isFormLoading, setIsFormLoading] = useState<boolean>(true)

  const paymentElementOptions: StripePaymentElementOptions = {
    layout: {
      type: 'tabs',
    },
  }

  useEffect(() => {
    if (!stripe || elements === null) {
      return
    }

    if (!clientSecret) {
      return
    }
    onStripeLoaded({ elements, stripe })
    stripe.retrieveSetupIntent(clientSecret).then(({ setupIntent }: any) => {
      switch (setupIntent.status) {
        case 'succeeded':
          onPaymentSuccess()
          break
        case 'processing':
          setMessage('Your payment method is processing.')
          break
        case 'requires_payment_method':
          setMessage('')
          break
        case 'canceled':
          setMessage('Your payment method was canceled')
          break
        default:
          setMessage('Something went wrong.')
          break
      }
    })
  }, [stripe])

  return (
    <form id="payment-form" style={{ overflowY: 'auto', overflowX: 'hidden', padding: '1px', minWidth: '500px' }}>
      {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
      {/* @ts-ignore */}
      <PaymentElement
        id="payment-element"
        options={paymentElementOptions}
        onChange={(event) => {
          setIsCardComplete && setIsCardComplete(event.complete)
        }}
      />
      <Box mt={2} />

      {showTaxForm && (
        <TaxForm
          taxDetails={taxDetails}
          handleTaxDetailsChange={handleTaxDetailsChange}
          setInvalidTaxDetails={setInvalidTaxDetails}
          isLoading={isFormLoading}
        />
      )}

      {showAddressForm && (
        <AddressElement
          options={{
            mode: 'billing',
            fields: {
              phone: 'always',
            },
            validation: {
              phone: {
                required: 'never',
              },
            },
            defaultValues: {
              name: customerProfile?.provider?.profile.name,
              phone: customerProfile?.provider?.profile.phone,
              address: customerProfile?.provider?.profile.address || undefined,
            },
          }}
          onChange={(event) => {
            const { value } = event
            const isRequired =
              value.name &&
              value.address?.line1 &&
              value.address?.city &&
              value.address?.postal_code &&
              value.address?.country

            setIsAddressComplete && setIsAddressComplete(!!isRequired)
          }}
          onReady={() => setIsFormLoading(false)}
        />
      )}
      {/* Show any error or success messages */}
      {message && (
        <Typography id="payment-message" color="red">
          {message}
        </Typography>
      )}
    </form>
  )
}
